import React from "react"
import { SEOHead } from "components/SEO"
import { generateFAQSchema } from "utils/structuredData"
import ArchitectureSection from "components/pages/hipaa-compliance/ArchitectureSection"
import ComplianceBadgesSection from "components/pages/hipaa-compliance/ComplianceBadgesSection"
import CTASection from "components/pages/hipaa-compliance/CTASection"
import DeploymentOptionsSection from "components/pages/hipaa-compliance/DeploymentOptionsSection"
import HeroSection from "components/pages/hipaa-compliance/HeroSection"
import SecurityFeaturesSection from "components/pages/hipaa-compliance/SecurityFeaturesSection"
import UseCasesSection from "components/pages/hipaa-compliance/UseCasesSection"
import Layout from "components/ui/Layout"

// FAQs for HIPAA compliance (used for schema markup)
const hipaaFaqs = [
  {
    question: "Is Preset HIPAA compliant?",
    answer:
      "Yes. Preset is fully HIPAA compliant with SOC 2 Type 2 certification and PCI-DSS Level 2 compliance. We implement comprehensive technical and administrative safeguards to protect PHI, including encryption, access controls, audit logging, and regular third-party security assessments.",
  },
  {
    question: "Does Preset offer a Business Associate Agreement (BAA)?",
    answer:
      "Yes. Preset provides Business Associate Agreements to healthcare organizations and covered entities. A BAA is available on Enterprise plans and can be executed before you connect any PHI data sources. Contact our sales team to get started.",
  },
  {
    question: "Does Preset store my protected health information (PHI)?",
    answer:
      "No. Preset uses a zero-copy architecture — it queries your data in place and never ingests or stores PHI. Only metadata like dashboard definitions, chart configurations, and user permissions are stored in Preset. Your PHI stays in your data warehouse behind your existing security controls.",
  },
  {
    question:
      "How does Preset help replace non-compliant analytics tools (shadow BI)?",
    answer:
      "Many healthcare teams resort to spreadsheets and ad-hoc tools that aren't HIPAA-compliant, creating compliance risk. Preset provides self-service dashboards and SQL exploration that give clinicians and ops teams the analytics they need while maintaining full regulatory compliance — eliminating shadow BI without slowing teams down.",
  },
  {
    question:
      "What deployment options are available for healthcare organizations?",
    answer:
      "Preset offers two HIPAA-compliant deployment options: Preset Cloud (fully managed SaaS on AWS with multi-tenant isolation) and Managed Private Cloud (dedicated deployment in your own AWS or GCP environment where data never leaves your VPC). Both options support BAAs.",
  },
  {
    question: "How does Preset protect data in transit and at rest?",
    answer:
      "All data in transit is secured with TLS 1.2 or higher. Metadata at rest is encrypted using AES-256. Query result caching uses workspace-specific AES-128 encryption keys and expires after 24 hours by default. Caching can be disabled entirely for sensitive datasets.",
  },
  {
    question: "Does Preset support SSO and role-based access controls?",
    answer:
      "Yes. Preset supports SAML SSO with Azure, Google, and Okta, plus SCIM for automated user provisioning. Role-Based Access Controls (RBAC) provide fine-grained permissions, and row-level security ensures users only see the PHI they're authorized to access — a key differentiator for multi-department healthcare environments.",
  },
  {
    question: "Is Preset ready for the 2026 HIPAA Security Rule changes?",
    answer:
      "Yes. The 2026 HIPAA Security Rule update introduces stricter requirements for ePHI access controls, audit logging, and security assessments. Preset already meets these standards through RBAC, comprehensive audit logging, continuous compliance monitoring with Vanta, and quarterly third-party penetration testing.",
  },
  {
    question: "Does Preset meet compliance requirements beyond HIPAA?",
    answer:
      "Yes. Preset maintains SOC 2 Type 2 certification, PCI-DSS Level 2 compliance, and HIPAA compliance simultaneously. For organizations operating internationally, Preset's architecture — where data stays in your infrastructure — also supports GDPR data residency requirements.",
  },
]

function HIPAACompliance({ location: { pathname } }) {
  return (
    <Layout
      contentSpaced
      trackingName="HIPAA Compliance"
      pageName="hipaa-compliance"
      pagePath={pathname}
    >
      <HeroSection />
      <ComplianceBadgesSection />
      <ArchitectureSection />
      <UseCasesSection />
      <SecurityFeaturesSection />
      <DeploymentOptionsSection />
      <CTASection />
    </Layout>
  )
}

// Export a Head component for Gatsby Head API
export function Head() {
  const faqSchema = generateFAQSchema(hipaaFaqs)

  return (
    <SEOHead
      title="HIPAA Compliant Analytics Platform"
      description="Preset is HIPAA compliant, enabling healthcare organizations to securely analyze protected health information (PHI) with an enterprise-grade open analytics platform and data visualization."
      pageUrl="https://preset.io/hipaa-compliance/"
      canonicalUrl="https://preset.io/hipaa-compliance/"
      structuredData={faqSchema}
    />
  )
}

export default HIPAACompliance
